import { useState } from "react";
import dayjs from "dayjs";
import { Promise as bluebird } from "bluebird";

import { faCalendarCheck, faRuler } from "@fortawesome/free-solid-svg-icons";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { letterSBoolean } from "@aclymatepackages/formatters";

import useCsvUploader from "./csvUploader";

import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import {
  createErrorObj,
  doesFieldExist,
  useSharedFormLoading
} from "../../helpers/components/inputs";
import { analyticsTrack } from "../../helpers/analytics";
import {
  useAccountData,
  useMutateCachedCollection
} from "../../helpers/firebase";
import { fetchOurApi } from "../../helpers/utils/apiCalls";

const fieldOptions = [
  { label: "Date (MM/DD/YYYY)", value: "dateCommon" },
  { label: "Electricity (kwh)", value: "unitKwh" },
  { label: "Gas (therms)", value: "unitTherms" },
  { label: "Gas (CCF)", value: "unitCcf" },
  { label: "Gas (MCF)", value: "unitMcf" },
  { label: "Gas (Cubic Meters)", value: "unitCubicMeters" },
  { label: "Gas (Gallons Propane)", value: "unitGallonsPropane" }
];

const requiredFields = [
  {
    label: "Date",
    description: "When was this utility emission?",
    icon: faCalendarCheck
  },
  {
    label: "Unit",
    description: "What unit of measure was used for this utility emission?",
    icon: faRuler
  }
];

const knownFields = [
  { header: "dateCommon", field: "dateCommon" },
  { header: "date", field: "dateCommon" },
  { header: "Kwh", field: "unitKwh" },
  { header: "Gallons Propane", field: "unitGallonsPropane" },
  { header: "Therms", field: "unitTherms" },
  { header: "Cubic Meters", field: "unitCubicMeters" },
  { header: "Ccf", field: "unitCcf" },
  { header: "Mcf", field: "unitMcf" }
];

const fieldValidator = (headers) => {
  if (
    !doesFieldExist(headers, "dateCommon") &&
    !doesFieldExist(headers, "dateMMMMYY")
  ) {
    return createErrorObj("Your CSV must include a Date field");
  }
  if (
    !doesFieldExist(headers, "unitKwh") &&
    !doesFieldExist(headers, "unitGallonsPropane") &&
    !doesFieldExist(headers, "unitTherms") &&
    !doesFieldExist(headers, "unitMcf") &&
    !doesFieldExist(headers, "unitCcf") &&
    !doesFieldExist(headers, "unitCubicMeters")
  ) {
    return createErrorObj("Your CSV must include a Unit field");
  }
  return { success: true };
};

const useProcessUtilities = ({
  fileInfo,
  setFileInfo,
  setUtilitiesResponseCount,
  setTotalUtilitiesCount,
  setOpen,
  selectedOfficeId,
  selectedOfficeName
}) => {
  const companyId = window.sessionStorage.getItem("accountId");
  const { csvId, name: fileName } = fileInfo;
  const { setFormLoading } = useSharedFormLoading();
  const [{ startDate, address }] = useAccountData();

  const mutateCachedCollection = useMutateCachedCollection();

  const { activateSnackbar } = usePlatformLayoutContext();

  const sendUtilitiesToBackend = async (utility) => {
    return await fetchOurApi({
      path: "/transactions/utility-sort",
      method: "POST",
      data: {
        utility,
        companyId,
        address,
        selectedOfficeId,
        selectedOfficeName
      },
      callback: (response) => {
        setUtilitiesResponseCount((currentCount) => currentCount + 1);
        const { success, finishedUtility } = response;
        if (success) {
          const dummyId = Math.random() * Date.now();
          return { ...finishedUtility, id: dummyId };
        }
        if (!success) {
          console.log("no success");
          return null;
        }
      }
    });
  };

  const sortOrExcludeUtilities = async (utility) => {
    const { date } = utility;

    if (startDate && dayjs(date).isBefore(startDate)) {
      setUtilitiesResponseCount((currentCount) => currentCount + 1);
      return { ...utility, beforeStartDate: true };
    }

    return sendUtilitiesToBackend(utility);
  };

  return async ({ data: utilities }) => {
    setFormLoading(true);

    editObjectData(setFileInfo, "csvId", csvId);

    analyticsTrack("Utilities Loaded", {
      numberOfUtilities: utilities.length
    });

    setTotalUtilitiesCount(utilities.length);

    const utilitiesAdditionalData = utilities.map((utility) => ({
      ...utility,
      source: "csv-upload",
      csvId,
      csvName: fileName,
      status: "confirmed",
      vendor: `${dayjs().format("MM/DD/YYYY")} CSV Upload Utilities Emissions`,
      name: `${dayjs().format("MM/DD/YYYY")} CSV Upload Utilities Emissions`,
      emissionProbability: "high"
    }));

    const newUtilities = await bluebird.map(
      utilitiesAdditionalData,
      sortOrExcludeUtilities,
      { concurrency: 10 }
    );

    const storedUtilities = newUtilities.filter(
      (utility) => utility && !utility?.duplicate && !utility?.beforeStartDate
    );

    mutateCachedCollection("transactions", (existingTransactions = []) => [
      ...existingTransactions,
      ...storedUtilities
    ]);

    const snackbarMessage = `${
      newUtilities.length
    } utility emission${letterSBoolean(
      newUtilities
    )} were added to your account`;
    activateSnackbar({ message: snackbarMessage, alert: "success" });

    setOpen(false);
    return setFormLoading(false);
  };
};

const useUtilitiesCsvUploader = ({
  open,
  setOpen,
  selectedOfficeId,
  selectedOfficeName
}) => {
  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [totalUtilitiesCount, setTotalUtilitiesCount] = useState(0);
  const [utilitiesResponseCounts, setUtilitiesResponseCount] = useState(0);

  const processData = useProcessUtilities({
    fileInfo,
    setFileInfo,
    setUtilitiesResponseCount,
    setTotalUtilitiesCount,
    setOpen,
    selectedOfficeId,
    selectedOfficeName
  });

  const csvUploaderSteps = useCsvUploader({
    knownFields,
    fieldOptions,
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    processData,
    fieldValidator,
    docType: "utilities",
    firstStepText:
      "Upload a CSV of your transactions and we'll automatically sort out the ones that you can offset.",
    requiredFields
  });

  return [
    csvUploaderSteps,
    (utilitiesResponseCounts / totalUtilitiesCount) * 100
  ];
};
export default useUtilitiesCsvUploader;
